
import type { UseMutationConfig } from 'react-relay';
import { useMutation } from 'react-relay';
import type { MutationParameters, PayloadError } from 'relay-runtime';
import { toast } from 'sonner';

type MutationResponseData = {
  error?: string | null;
  errors?: string[];
  success?: string | null;
};

type SafeValues = Record<string, unknown> | null;

export const getError = (data: MutationResponseData | null | undefined): string | null => {
  if (data?.error) {
    return data.error;
  }

  if (data?.errors && Array.isArray(data.errors)) {
    return data.errors.join('\n');
  }

  return null;
};

export type UseMutationCallbacksArgs<TMutation extends MutationParameters> = {
  mutation: UseMutationConfig<TMutation>['mutation'];
  name: keyof TMutation['response'];
  success?: string;
  error?: string;
  shouldShowSnackbar?: boolean;
  afterCompleted?: (
    response: TMutation['response'],
    errors: Array<PayloadError> | null,
    safeValues?: SafeValues,
  ) => void;
  afterError?: (
    error: Error | string,
    safeValues?: SafeValues,
    response?: TMutation['response']
  ) => void;
};

export const useMutationCallbacks = <TMutation extends MutationParameters>(
  args: UseMutationCallbacksArgs<TMutation>,
): [
  (executeConfig?: UseMutationConfig<TMutation>, safeValues?: SafeValues) => void,
  boolean
] => {
  const {
    mutation,
    name,
    afterCompleted,
    afterError,
    shouldShowSnackbar = true,
  } = args;

  const [execute, isPending] = useMutation(mutation);

  const onCompleted = (
    errorMessage = args.error,
    successMessage = args.success,
    safeValues: SafeValues
  ) => (response: TMutation['response']) => {
    const data = response[name] as MutationResponseData | null | undefined;

    if (!data || data.error || (data.errors && data.errors.length > 0)) {
      const errorText = getError(data) || errorMessage || 'An error occurred';

      if (shouldShowSnackbar) {
        toast.error(errorText);
      }

      afterError?.(
        new Error(errorText),
        safeValues || {},
        response
      );

      return;
    }

    const finalSuccessMessage = (data as MutationResponseData)?.success || successMessage;

    if (finalSuccessMessage && shouldShowSnackbar) {
      toast.success(finalSuccessMessage);
    }

    afterCompleted?.(response, null, safeValues);
  };

  const onError = (
    errorMessage = args.error,
    safeValues: SafeValues
  ) => (errorMutation: Error) => {
    const finalErrorMessage = errorMessage || errorMutation.message || 'An error occurred';

    if (shouldShowSnackbar) {
      toast.error(finalErrorMessage);
    }

    afterError?.(errorMutation, safeValues);
  };

  const executeFn = (
    executeConfig: UseMutationConfig<TMutation> = { variables: {} as TMutation['variables'] },
    safeValues: SafeValues = null
  ) => {
    return execute({
      onCompleted: onCompleted(executeConfig.error, executeConfig.success, safeValues),
      onError: onError(executeConfig.error, safeValues),
      ...executeConfig,
    });
  };

  return [executeFn, isPending];
};
